import { offset } from '@/utils/offset.js'
import maInput from '@/components/input'

/**
 * @module
 * @name InputSelectUsers
 * @extends Input
 */
export default {
  name: 'InputSelectUsers',
  extends: maInput,
  /**
   * @function
   * @property {Boolean} focus
   * @property {Boolean} dropdownUpward
   * @property {String} optionFocused
   * @property {*} optionNull
   */
  data: function () {
    return {
      focus: false,
      dropdownUpward: false,
      optionFocused: '',
      optionNull: null
    }
  },

  /**
   * @property {String} name Native HTML element name.
   * @property {String} icon VueSVGicon name.
   * @property {Array} options Array of options. Options should contains at least
   *                           key and value.
   * @property {Boolean} nullValue If true, a null selection are added to computed
   *                               options, used to unselect any option.
   */
  props: {
    name: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    nullValue: {
      type: Boolean,
      required: false
    }
  },

  /**
   * @property {Array} comOptions Array of options. If nullValue, a null option
   *                              is added.
   * @property {String} selectedOption Selected option key
   * @property {String} selectedLiteral Selected option literal
   */
  computed: {
    comOptions: function () {
      if (this.options && this.options.length > 0 &&
          this.nullValue && !this.optionNull) {
        const optionNull = { key: null, literal: this.$t('common.noSelected') }
        return [optionNull, ...this.options]
      } else {
        return this.options
      }
    },
    selectedOption: function () {
      return this.value ? this.comOptions.find(option => parseInt(option.key) === parseInt(this.value)) : null
    },
    selectedLiteral: function () {
      if (this.comOptions.length <= 0) {
        return this.$t('common.noOptions')
      } else {
        if (this.nullValue) {
          return this.selectedOption ? this.selectedOption.literal : this.$t('common.noSelected')
        } else {
          return this.selectedOption ? this.selectedOption.literal : this.selectOption(this.comOptions[0])
        }
      }
    }
  },

  methods: {
    /**
     * @function
     * @name setDefaultValue
     * @description Set default value if exists. This function finds it in order
     *              to get key, value tupla in existing options. If there isn't
     *              any value, first value is selected.
     */
    setDefaultValue: function () {
      if (this.comOptions && this.comOptions.length > 0) {
        if (this.value) {
          const selectedOption = this.comOptions.filter(option => parseInt(option.key) === parseInt(this.value))
          if (selectedOption.length > 0) {
            this.comOptionselected = selectedOption[0]
            this.optionFocused = selectedOption[0]
          } else {
            this.selectFirstOption()
          }
        } else {
          this.selectFirstOption()
        }
      } else {
        this.comOptionselected = null
      }
    },

    /**
     * @function
     * @name ableKeyNav
     */
    ableKeyNav () {
      this.optionFocused = this.comOptionselected

      if (this.focus === false) {
        this.handleDropdown()
      }
    },

    /**
     * @function
     * @name selectFirstOption
     * @description Set first option and focus it.
     */
    selectFirstOption () {
      this.comOptionselected = this.comOptions[0]
      this.optionFocused = this.comOptions[0]
    },

    /**
     * @function
     * @name handleFocus
     */
    handleFocus: function () {
      this.focus = !this.focus
    },

    /**
     * @function
     * @name isFocus
     * @param {String} optionKey Option key
     * @description Method that returns if option passed as param is the same as
     *              focused.
     */
    isFocus: function (optionKey) {
      return optionKey === this.optionFocused.key
    },

    /**
     * @function
     * @param {Object} event
     * @description Navigates dropdown selecting above option.
     */
    keyNavUp (event) {
      event.preventDefault()
      let optionSelectedIndx = this.comOptions.findIndex(option => option.key === this.optionFocused.key)
      if (optionSelectedIndx > 0) {
        this.optionFocused = this.comOptions[optionSelectedIndx - 1]
      }
    },

    /**
     * @function
     * @param {Object} event
     * @description Navigates dropdown selecting below option.
     */
    keyNavDown (event) {
      event.preventDefault()
      let optionSelectedIndx = this.comOptions.findIndex(option => option.key === this.optionFocused.key)

      if (optionSelectedIndx < this.comOptions.length - 1) {
        this.optionFocused = this.comOptions[optionSelectedIndx + 1]
      }
    },

    /**
     * @function
     * @description Select value on enter and blur input.
     */
    updateValueOnEnter () {
      this.$el.querySelector('input').blur()
      this.selectOption(this.optionFocused)
    },

    /**
     * @function
     * @param {Object} option.key Option key.
     * @param {Object} option.value Option value.
     * @description Select param option as selected.
     */
    selectOption (option) {
      this.showOptions = false
      this.comOptionselected = option
      this.$emit('change', option.key)
    },

    /**
     * @function
     * @name dropdownToTop
     */
    dropdownToTop () {
      let optionHeight = 48
      let totalInputs = this.$el.querySelectorAll('.input--select__dropdown li').length
      let totalHeight
      let inputHeight = offset(this.$el.querySelector('.input__el')).top
      let inputTop = this.$el.querySelector('.input__el').scrollTop
      let inputBottom = inputTop + inputHeight
      let windowHeight = window.innerHeight
      let windowTop = document.documentElement.scrollTop
      let windowBottom = windowTop + windowHeight
      let inputToBottom = windowBottom - inputBottom

      if (optionHeight * totalInputs > 250) {
        totalHeight = 250
      } else {
        totalHeight = optionHeight * totalInputs
      }

      if (totalHeight + 48 > inputToBottom - 172) {
        this.dropdownUpward = true
      } else {
        this.dropdownUpward = false
      }
    },

    /**
     * @function
     * @name handleDropdown
     */
    handleDropdown: function () {
      let dropdown = this.$el.querySelector('.input--select__dropdown')
      let inputHeight = this.$el.querySelector('.input__el').clientHeight
      let inputTop = offset(this.$el.querySelector('.input__el')).top
      let inputBottom = inputTop + inputHeight
      let windowHeight = window.innerHeight
      let windowTop = document.documentElement.scrollTop
      let windowBottom = windowTop + windowHeight
      if (this.focus === true) {
        if (this.dropdownUpward === true) {
          dropdown.style.maxHeight = (inputTop - windowTop) + 'px'
        } else {
          dropdown.style.maxHeight = ((windowBottom - 160) - inputBottom) + 'px'
        }
      } else {
        dropdown.style.maxHeight = '0px'
      }
    }
  },
  /**
   * @property {function} focus Handle dropdown on focus.
   * @property {function} options Watch options. If change and there isn't any
   *                              option selected, set first option.
   */
  watch: {
    focus: function () {
      this.handleDropdown()
    },
    options: function (value) {
      if (!this.comOptionselected) this.selectFirstOption()
    }
  }
}
